import { useEffect, useRef, useState } from "react";
import {
  FaChartLine,
  FaBoxes,
  FaUsers,
  FaFileAlt,
  FaShoppingCart,
  FaBrain,
  FaStore,
  FaSyncAlt,
  FaClipboardList,
  FaChartPie,
  FaRocket,
  FaArrowRight,
  FaWarehouse,
  FaTruck,
} from "react-icons/fa";
import "./AboutSection.css";
import PremiumFooter from "./PremiumFooter";

const featureCards = [
  {
    title: "Dashboard Analytics",
    desc: "Track live performance with clear insights and a focused overview of your business.",
    icon: <FaChartLine />,
  },
  {
    title: "Inventory Tracking",
    desc: "Monitor stock levels, movement, and availability across every workflow in one place.",
    icon: <FaBoxes />,
  },
  {
    title: "Team Collaboration",
    desc: "Align teams with shared visibility, faster updates, and smoother coordination.",
    icon: <FaUsers />,
  },
  {
    title: "Smart Reports",
    desc: "Generate refined reports that highlight trends, performance, and key changes quickly.",
    icon: <FaFileAlt />,
  },
  {
    title: "Order Management",
    desc: "Keep orders organized from placement to dispatch with less friction and more control.",
    icon: <FaShoppingCart />,
  },
  {
    title: "AI Insights",
    desc: "Use intelligent summaries to spot patterns, forecast needs, and make better decisions.",
    icon: <FaBrain />,
  },
];

const workflowSteps = [
  {
    step: "01",
    title: "Connect Store",
    desc: "Link your marketplaces and storefronts in a single, seamless workspace.",
    accent: "Integrations ready",
    icon: <FaStore />,
    visualTitle: "Marketplace Connections",
    visualCopy: "All stores synced into one command layer.",
    visualType: "connect",
  },
  {
    step: "02",
    title: "Sync Inventory",
    desc: "Keep stock, warehouses, and availability aligned in real time.",
    accent: "Inventory live",
    icon: <FaSyncAlt />,
    visualTitle: "Live Stock Flow",
    visualCopy: "Every unit tracked with calm, precise updates.",
    visualType: "inventory",
  },
  {
    step: "03",
    title: "Manage Orders",
    desc: "Process every order from intake to dispatch with zero clutter.",
    accent: "Order queue",
    icon: <FaClipboardList />,
    visualTitle: "Order Command Center",
    visualCopy: "Status, fulfillment, and dispatch in one view.",
    visualType: "orders",
  },
  {
    step: "04",
    title: "Track Analytics",
    desc: "Read performance signals with elegant reports and clear trends.",
    accent: "Analytics pulse",
    icon: <FaChartPie />,
    visualTitle: "Performance Insights",
    visualCopy: "Charts and metrics that surface what matters most.",
    visualType: "analytics",
  },
  {
    step: "05",
    title: "Scale Business",
    desc: "Use confident insights to expand operations and grow sustainably.",
    accent: "Growth mode",
    icon: <FaRocket />,
    visualTitle: "Scaling Momentum",
    visualCopy: "Growth metrics built to support your next leap.",
    visualType: "scale",
  },
];

export default function AboutSection() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.25,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section className="about-section" ref={sectionRef}>
        <div className="about-video-card">
          
          <video
            className="about-card-video"
            autoPlay
            muted
            loop
            playsInline
          >
            <source
              src="https://techiohisab.com/about.mp4"
              type="video/mp4"
            />
          </video>

          {/* overlay content */}
          <div className="about-overlay">

            {/* left card */}
            <div className={`about-mini-card ${isVisible ? "show-animation" : ""}`}>
              <div className="about-mini-top">
                <div className="dots">
                  <span />
                  <span />
                  <span />
                </div>

                <div className="live-pill">LIVE</div>
              </div>

              <div className="chart">
                <span style={{ height: "40%" }} />
                <span style={{ height: "70%" }} />
                <span style={{ height: "52%" }} />
                <span style={{ height: "82%" }} />
              </div>

              <h3>Unified workspace</h3>
              <p>Calm visibility for fast-moving teams.</p>
            </div>

            {/* right content */}
            <div className={`about-content ${isVisible ? "show-animation" : ""}`}>
              <h1>Built For Teams That Move Fast</h1>

              <p>
                Bring dashboards, workflows, and insights into one refined
                workspace that feels fast, clear, and reliable.
              </p>

              <div className="feature-list">
                <div className="feature-item">
                  <h4>Ultra-Low Latency</h4>
                  <p>Real-time updates and faster decisions.</p>
                </div>

                <div className="feature-item">
                  <h4>Collaborative Workspaces</h4>
                  <p>Shared visibility and team coordination.</p>
                </div>

                <div className="feature-item">
                  <h4>Predictive Insights</h4>
                  <p>Smart summaries for confident planning.</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
      <FeatureSection />
      <WorkflowSection />
      <PremiumFooter />
    </>
  );
}

function FeatureSection() {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.2,
      }
    );

    observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section className="features-section" ref={sectionRef}>
      <div className={`features-header ${isVisible ? "show-animation" : ""}`}>
        <h2>Powerful Tools For Smarter Business Management</h2>
        <p>
          Manage operations, inventory, analytics, teams, and workflows from one intelligent
          dashboard.
        </p>
      </div>

      <div className="features-grid">
        {featureCards.map((card, index) => (
          <article
            className={`feature-card ${isVisible ? "show-animation" : ""}`}
            key={card.title}
            style={{ transitionDelay: `${0.1 + index * 0.08}s` }}
          >
            <div className="feature-card-icon" aria-hidden="true">
              {card.icon}
            </div>
            <h3>{card.title}</h3>
            <p>{card.desc}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

function WorkflowSection() {
  const sectionRef = useRef(null);
  const panelRefs = useRef([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        threshold: 0.15,
      }
    );

    sectionObserver.observe(section);

    return () => sectionObserver.disconnect();
  }, []);

  useEffect(() => {
    const nodes = panelRefs.current.filter(Boolean);
    if (!nodes.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (!visibleEntries.length) return;

        const topEntry = visibleEntries.reduce((best, entry) =>
          entry.intersectionRatio > best.intersectionRatio ? entry : best
        );

        const nextIndex = Number(topEntry.target.getAttribute("data-step-index") || 0);
        setActiveStep(nextIndex);
      },
      {
        threshold: [0.55],
        rootMargin: "-20% 0px -20% 0px",
      }
    );

    nodes.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, []);

  return (
    <section className={`workflow-section ${isVisible ? "show-animation" : ""}`} ref={sectionRef}>
      <div className="workflow-layout">
        <aside className="workflow-sidebar">
          <div className="workflow-header">
            <span className="workflow-label">WORKFLOW</span>
            <h2>Sticky Workflow That Turns Operations Into A Smooth Story</h2>
         
          </div>

          <div className="workflow-sticky">
            {workflowSteps.map((step, index) => {
              const isActive = index === activeStep;
              return (
                <div className={`workflow-step ${isActive ? "active" : ""}`} key={step.step}>
                  <span className="workflow-step-index">{step.step}</span>
                  <div className="workflow-step-copy">
                    <h3>{step.title}</h3>
                    <p>{step.desc}</p>
                  </div>
                  <span className="workflow-step-glow" aria-hidden="true" />
                </div>
              );
            })}
          </div>
        </aside>

        <div className="workflow-panels">
          {workflowSteps.map((step, index) => (
            <article
              className={`workflow-panel ${index === activeStep ? "active" : ""}`}
              key={step.step}
              ref={(node) => {
                panelRefs.current[index] = node;
              }}
              data-step-index={index}
            >
              <div className="workflow-panel-copy">
                <span className="workflow-panel-badge">{step.accent}</span>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </div>

              <div className="workflow-visual-shell">
                <WorkflowVisual step={step} index={index} />
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function WorkflowVisual({ step, index }) {
  const variants = {
    connect: (
      <div className="workflow-visual workflow-visual-connect">
        <div className="workflow-float-card workflow-float-card-left">
          <span className="workflow-mini-label">Amazon</span>
          <strong>Connected</strong>
        </div>
        <div className="workflow-link-line" aria-hidden="true">
          <span />
        </div>
        <div className="workflow-float-card workflow-float-card-right">
          <span className="workflow-mini-label">Flipkart</span>
          <strong>Live Sync</strong>
        </div>
      </div>
    ),
    inventory: (
      <div className="workflow-visual workflow-visual-inventory">
        <div className="workflow-stock-card">
          <div className="workflow-stock-chart">
            <span style={{ height: "36%" }} />
            <span style={{ height: "58%" }} />
            <span style={{ height: "42%" }} />
            <span style={{ height: "74%" }} />
            <span style={{ height: "52%" }} />
          </div>
          <div className="workflow-stock-meta">
            <span className="workflow-meta-icon" aria-hidden="true">
              <FaWarehouse />
            </span>
            <strong>Warehouse Balance</strong>
            <p>Healthy stock levels across locations.</p>
          </div>
        </div>
      </div>
    ),
    orders: (
      <div className="workflow-visual workflow-visual-orders">
        <div className="workflow-order-card">
          <div className="workflow-order-row">
            <span>#TH-1042</span>
            <em>Packed</em>
          </div>
          <div className="workflow-order-row">
            <span>#TH-1048</span>
            <em>Dispatching</em>
          </div>
          <div className="workflow-order-row">
            <span>#TH-1051</span>
            <em>Delivered</em>
          </div>
          <div className="workflow-order-footer">
            <FaTruck aria-hidden="true" />
            <span>Dispatch pipeline active</span>
          </div>
        </div>
      </div>
    ),
    analytics: (
      <div className="workflow-visual workflow-visual-analytics">
        <div className="workflow-analytics-card">
          <div className="workflow-analytics-top">
            <strong>Revenue Trend</strong>
            <span>+18.2%</span>
          </div>
          <div className="workflow-analytics-line" aria-hidden="true">
            <span />
          </div>
        </div>
      </div>
    ),
    scale: (
      <div className="workflow-visual workflow-visual-scale">
        <div className="workflow-scale-card">
          <div className="workflow-scale-ring">
            <span>82%</span>
          </div>
          <strong>Growth Momentum</strong>
          <p>Ready for your next expansion cycle.</p>
        </div>
      </div>
    ),
  };

  return (
    <div className="workflow-visual-stage" data-variant={step.visualType}>
      <div className={`workflow-visual-frame ${index % 2 === 0 ? "alt" : ""}`}>
        {variants[step.visualType]}
      </div>
      <div className="workflow-visual-caption">
        <span>{step.visualTitle}</span>
        <p>{step.visualCopy}</p>
      </div>
      <div className="workflow-panel-arrow" aria-hidden="true">
        <FaArrowRight />
      </div>
    </div>
  );
}

